import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { clientAPI } from "./axiosAnstance";
import axios from "axios";
import { SERVER_URL } from "../../../serverConfig";
import { getAuthHeader } from "../../../jwt/config";
import fileDownload from "js-file-download";

// --- THUNKS ---

/**
 * Récupère la liste paginée des demandes d'indemnités
 */
export const listDemandeIndemniteApi = createAsyncThunk(
  "indemnites/demande",
  async ({ page = 0, size = 10 } = {}, thunkAPI) => {
    try {
      const url = `/api/indemnites/getDemandes?page=${page}&size=${size}`;
      const response = await clientAPI.get(url);
      return response.data; // Doit être un objet Page (content, totalElements, etc.)
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  }
);

/**
 * Filtre les demandes d'indemnités avec pagination
 */
export const listIndemniteFilterAPI = createAsyncThunk(
  "indemnites/filter",
  async (data, thunkAPI) => {
    try {
      const page = data.page || 0;
      const size = data.size || 10;
      const filters = { ...data };
      delete filters.page;
      delete filters.size;

      const url = `/api/indemnites/listFilter?page=${page}&size=${size}`;
      const response = await clientAPI.post(url, filters);
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const listTypeFichierIndemniteSavedAPI = createAsyncThunk(
  "indemnites/listeTypeFichierSaved",
  async (idDemande) => {
    const response = await clientAPI.get(`/api/indemnites/listeTypeFichierSaved/${idDemande}`);
    return response.data;
  }
);

export const listIndemnitesDemandeTraiteAPI = createAsyncThunk(
  "indemnites/listeDemandeTraite",
  async () => {
    const url = `/api/indemnites/listeDemandeTraite`;
    const response = await clientAPI.get(url);
    return response.data;
  }
);

export const traiteDemandeIndemnitesAPI = createAsyncThunk(
  "indemnites/traiteDemande",
  async ({ id }, thunkAPI) => {
    try {
      const response = await clientAPI.post(`/api/indemnites/traiteDemande/${id}`);
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const refuserIndemniteAPI = createAsyncThunk(
  "indemnites/refusDemande",
  async (data) => {
    const formData = new FormData();
    formData.append("idDemande", data.idDemande);
    formData.append("motifRefus", data.motifRefus);
    const response = await clientAPI.post("/api/indemnites/refusDemande", formData);
    return response.data;
  }
);

export const validationIndemnitesAPI = createAsyncThunk(
  "indemnites/validation",
  async ({ id }, thunkAPI) => {
    try {
      const response = await clientAPI.post(`/api/indemnites/validation/${id}`);
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const incompteteIndemnitesAPI = createAsyncThunk(
  "indemnites/icompleteDemande",
  async (data) => {
    const formData = new FormData();
    formData.append("idDemande", data.idDemande);
    formData.append("motifRefus", data.motifRefus);
    const response = await clientAPI.post("/api/indemnites/icompleteDemande", formData);
    return response.data;
  }
);

export const downloadExportIndemnitesAPI = createAsyncThunk(
  "indemnites/exportData",
  async (data) => {
    const token = localStorage.getItem("token");
    const d = new Date();
    const datestring = d.toISOString().split('T')[0];
    console.log('data de excel ::  ',data)
    const response = await axios.post(
      `${SERVER_URL}/api/indemnites/export-data`,
      data,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        responseType: "blob",
      }
    );

    fileDownload(response.data, `Soutien social -${datestring}.xlsx`);
  }
);

export const getFileByDemandeAndTypeIndemnites = createAsyncThunk(
  "indemnites/getFile",
  async (data) => {
    const res = await axios.get(
      `${SERVER_URL}/api/indemnites/getFileByDemandeAndType/${data.idDemande}/${data.id_type_fichier}`,
      {
        headers: getAuthHeader(),
        responseType: "blob",
      }
    );
    return res.data;
  }
);

// --- SLICE ---

const IndemnitesSlice = createSlice({
  name: "indemnites",
  initialState: {
    listDemande: [],        // Initialisé à vide (plus d'undefined)
    listDemandeTraite: [],
    listFichierSaved: [],
    totalElements: 0,       // Initialisé à 0
    pageNumber: 0,
    pageSize: 10,
    status: 'idle',
    error: null,
  },
  reducers: {
    // Vous pouvez ajouter des reducers synchrones ici si besoin
  },
  extraReducers: (builder) => {
    // Fonction utilitaire pour gérer la réponse de pagination (évite la répétition)
    const handlePaginationFulfilled = (state, action) => {
      state.status = 'succeeded';
      // Sécurité : si content est undefined, on met un tableau vide
      state.listDemande = action.payload?.content || [];
      state.totalElements = action.payload?.totalElements || 0;
      state.pageNumber = action.payload?.number || 0;
      state.pageSize = action.payload?.size || 10;
    };

    builder
      // --- Listes Paginées (Standard et Filtre) ---
      .addCase(listDemandeIndemniteApi.pending, (state) => {
        state.status = "loading";
      })
      .addCase(listDemandeIndemniteApi.fulfilled, handlePaginationFulfilled)
      .addCase(listDemandeIndemniteApi.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || action.error.message;
      })

      .addCase(listIndemniteFilterAPI.pending, (state) => {
        state.status = "loading";
      })
      .addCase(listIndemniteFilterAPI.fulfilled, handlePaginationFulfilled)
      .addCase(listIndemniteFilterAPI.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || action.error.message;
      })

      // --- Autres Actions ---
      .addCase(listIndemnitesDemandeTraiteAPI.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.listDemandeTraite = action.payload || [];
      })
      .addCase(listTypeFichierIndemniteSavedAPI.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.listFichierSaved = action.payload || [];
      });
  },
});

export default IndemnitesSlice.reducer;
